import XLSX from "xlsx-js-style";
import moment from "moment";
import { toast } from "../../common_components/Toast/ToastUtils";
import { formatValue, percentChange } from "./reportBuilder.utils";
import { getNetwork } from "./reportBuilder.networks";
/**
 * Report Builder - Excel export, in the Divergence Report export's design:
 *
 *   · title, client and date range (with "vs." the compare range) on top
 *   · one block per table: a grey network header, a column header row, then
 *     every row
 *   · with compare on, each metric is followed by a "% CHANGE" column, and every
 *     row has a lighter compare row directly under it (compare values, no change)
 *   · a Summary row (and its compare row) closing each block
 *
 * Built from the report on screen, so the file matches the preview. The style
 * objects mirror `DivergenceReport/exportDivergenceExcel.ts`, which is left
 * untouched.
 */
// ═══════════════════════════════════════════════════════════
// STYLES (same as the Divergence export)
// ═══════════════════════════════════════════════════════════
const thin = (rgb) => ({ style: "thin", color: { rgb } });
const boxBorder = (rgb) => ({ top: thin(rgb), bottom: thin(rgb), left: thin(rgb), right: thin(rgb) });
const titleStyle = {
    font: { bold: true, sz: 20, color: { rgb: "181D27" } },
    alignment: { horizontal: "center", vertical: "center" },
    border: { bottom: thin("E5E7EB") },
};
const clientNameStyle = {
    font: { bold: true, sz: 14, color: { rgb: "374151" } },
    alignment: { horizontal: "center", vertical: "center" },
};
const dateRangeStyle = {
    font: { bold: false, sz: 12, color: { rgb: "6B7280" } },
    alignment: { horizontal: "center", vertical: "center" },
    border: { bottom: { style: "medium", color: { rgb: "D1D5DB" } } },
};
const networkHeaderStyle = {
    font: { bold: true, sz: 12, color: { rgb: "181D27" } },
    fill: { fgColor: { rgb: "E5E7EB" } },
    alignment: { horizontal: "left", vertical: "center" },
    border: boxBorder("D1D5DB"),
};
const columnHeaderStyle = {
    font: { bold: true, sz: 11, color: { rgb: "374151" } },
    fill: { fgColor: { rgb: "F3F4F6" } },
    alignment: { horizontal: "center", vertical: "center" },
    border: boxBorder("D1D5DB"),
};
const currentRowStyle = {
    font: { bold: false, sz: 11, color: { rgb: "181D27" } },
    fill: { fgColor: { rgb: "FFFFFF" } },
    alignment: { horizontal: "left", vertical: "center" },
    border: boxBorder("E5E7EB"),
};
const comparedRowStyle = {
    font: { bold: false, sz: 10, color: { rgb: "6B7280" } },
    fill: { fgColor: { rgb: "F9FAFB" } },
    alignment: { horizontal: "left", vertical: "center" },
    border: boxBorder("E5E7EB"),
};
const summaryRowStyle = {
    font: { bold: true, sz: 11, color: { rgb: "181D27" } },
    fill: { fgColor: { rgb: "F3F4F6" } },
    alignment: { horizontal: "left", vertical: "center" },
    border: {
        top: { style: "medium", color: { rgb: "D1D5DB" } },
        bottom: { style: "medium", color: { rgb: "D1D5DB" } },
        left: thin("D1D5DB"),
        right: thin("D1D5DB"),
    },
};
const summaryComparedStyle = {
    font: { bold: true, sz: 10, color: { rgb: "6B7280" } },
    fill: { fgColor: { rgb: "F9FAFB" } },
    alignment: { horizontal: "left", vertical: "center" },
    border: {
        top: thin("D1D5DB"),
        bottom: { style: "medium", color: { rgb: "D1D5DB" } },
        left: thin("D1D5DB"),
        right: thin("D1D5DB"),
    },
};
/** The label column(s): bold, with a heavier right edge, like Divergence's date column. */
const dateColumnStyle = {
    font: { bold: true, sz: 11, color: { rgb: "181D27" } },
    fill: { fgColor: { rgb: "FFFFFF" } },
    alignment: { horizontal: "left", vertical: "center" },
    border: { ...boxBorder("E5E7EB"), right: { style: "medium", color: { rgb: "D1D5DB" } } },
};
const comparedDateStyle = {
    font: { bold: false, sz: 10, color: { rgb: "6B7280" } },
    fill: { fgColor: { rgb: "F9FAFB" } },
    alignment: { horizontal: "left", vertical: "center" },
    border: { ...boxBorder("E5E7EB"), right: { style: "medium", color: { rgb: "D1D5DB" } } },
};
const emptyStyle = { fill: { fgColor: { rgb: "FFFFFF" } } };
/** Green for up, red for down - the Divergence change cell. */
const changeCellStyle = (baseStyle, change) => ({
    ...baseStyle,
    font: { ...baseStyle.font, color: { rgb: change >= 0 ? "16A34A" : "DC2626" } },
});
const DASH = "-";
/** Same wording as the Divergence export: whole-number change, colour shows the direction. */
const formatChange = (change) => change === null || Number.isNaN(change) ? DASH : `${Math.abs(change).toFixed(0)}%`;
const cell = (v, s) => ({ v, t: "s", s });
const fillTo = (row, width, s) => {
    for (let i = row.length; i < width; i += 1)
        row.push(cell("", s));
};
/** Depth-first, every node open - the file has no collapsed rows. */
const flatten = (rows) => rows.flatMap((row) => [row, ...(row.children?.length ? flatten(row.children) : [])]);
/** Network names behind a table's metric columns - its header, as Divergence names a network. */
const networkTitle = (report) => {
    const keys = [];
    report.columns.forEach((column) => {
        if (column.network && !keys.includes(column.network))
            keys.push(column.network);
    });
    const labels = keys.map((key) => getNetwork(key)?.label || key);
    return labels.length ? labels.join(" + ") : "Report";
};
const sectionsOf = (report) => report.tables?.length
    ? report.tables.map((table) => ({ title: table.label, report: table.report }))
    : [{ title: networkTitle(report), report }];
const columnsOf = (report) => ({
    dimensionColumns: report.columns.filter((column) => column.is_dimension),
    metricColumns: report.columns.filter((column) => !column.is_dimension),
});
/** Dimension columns + each metric (+ its % CHANGE when comparing). */
const widthOf = (report) => {
    const { dimensionColumns, metricColumns } = columnsOf(report);
    const compare = hasCompareOn(report);
    return dimensionColumns.length + metricColumns.length * (compare ? 2 : 1);
};
const hasCompareOn = (report) => Boolean(report.compare_totals) || flatten(report.rows).some((row) => Boolean(row.compare_values));
const dimensionKey = (column) => column.id.replace("__dimension__", "");
// ═══════════════════════════════════════════════════════════
// MAIN EXPORT FUNCTION
// ═══════════════════════════════════════════════════════════
export const exportReportExcel = (report, options = {}) => {
    if (!report) {
        toast.info("No data available to export");
        return;
    }
    try {
        const sections = sectionsOf(report).filter((section) => section.report.rows.length > 0 || section.report.columns.some((c) => !c.is_dimension));
        if (sections.length === 0) {
            toast.info("No data available to export");
            return;
        }
        const allRows = [];
        const merges = [];
        const maxColumns = Math.max(6, ...sections.map((section) => widthOf(section.report)));
        const blankRow = () => {
            const row = [];
            fillTo(row, maxColumns, emptyStyle);
            return row;
        };
        const bannerRow = (text, style) => {
            const row = [cell(text, style)];
            fillTo(row, maxColumns, style);
            merges.push({ s: { r: allRows.length, c: 0 }, e: { r: allRows.length, c: maxColumns - 1 } });
            allRows.push(row);
        };
        // ── Top: title, client, date range ─────────────────────────────────
        bannerRow(options.reportName || "Report", titleStyle);
        bannerRow(options.clientName || "", clientNameStyle);
        let dateRangeText = "";
        if (options.startDate && options.endDate) {
            const fmt = (iso) => moment(iso).format("MM/DD/YYYY");
            dateRangeText = `${fmt(options.startDate)} - ${fmt(options.endDate)}`;
            if (options.compareStartDate && options.compareEndDate) {
                dateRangeText += `  vs.  ${fmt(options.compareStartDate)} - ${fmt(options.compareEndDate)}`;
            }
        }
        bannerRow(dateRangeText, dateRangeStyle);
        allRows.push(blankRow());
        // ── One block per table ────────────────────────────────────────────
        sections.forEach((section, index) => {
            const { report: table } = section;
            const { dimensionColumns, metricColumns } = columnsOf(table);
            const compare = hasCompareOn(table);
            if (index > 0)
                allRows.push(blankRow());
            // Network header.
            bannerRow(section.title, networkHeaderStyle);
            // Column headers.
            const header = dimensionColumns.map((column) => cell(column.label.toUpperCase(), { ...columnHeaderStyle, alignment: { horizontal: "left", vertical: "center" } }));
            metricColumns.forEach((column) => {
                header.push(cell(column.label.toUpperCase(), columnHeaderStyle));
                if (compare)
                    header.push(cell("% CHANGE", columnHeaderStyle));
            });
            fillTo(header, maxColumns, columnHeaderStyle);
            allRows.push(header);
            // A cell's text for one metric of one row of values.
            const valueOf = (values, column) => {
                const value = values?.[column.id];
                return value === undefined || value === null ? DASH : formatValue(value, column.format);
            };
            // Rows: the current row, then its compare row.
            flatten(table.rows).forEach((row) => {
                // A day only the (longer) compare range has: no current values to show.
                if (!(compare && row.compare_only)) {
                    const current = dimensionColumns.map((column) => cell(row.dimensions[dimensionKey(column)] ?? "", dateColumnStyle));
                    metricColumns.forEach((column) => {
                        current.push(cell(valueOf(row.values, column), currentRowStyle));
                        if (!compare)
                            return;
                        const now = row.values[column.id];
                        const then = row.compare_values?.[column.id];
                        const change = now !== undefined && then !== undefined ? percentChange(now, then) : null;
                        current.push(cell(formatChange(change), change !== null ? changeCellStyle(currentRowStyle, change) : currentRowStyle));
                    });
                    fillTo(current, maxColumns, currentRowStyle);
                    allRows.push(current);
                }
                if (compare && row.compare_values) {
                    const compared = dimensionColumns.map((column) => cell(row.compare_dimensions?.[dimensionKey(column)] ?? "", comparedDateStyle));
                    metricColumns.forEach((column) => {
                        compared.push(cell(valueOf(row.compare_values, column), comparedRowStyle));
                        compared.push(cell("", comparedRowStyle));
                    });
                    fillTo(compared, maxColumns, comparedRowStyle);
                    allRows.push(compared);
                }
            });
            // Summary + its compare row.
            const summary = dimensionColumns.map((_, i) => cell(i === 0 ? "Summary" : "", summaryRowStyle));
            metricColumns.forEach((column) => {
                summary.push(cell(valueOf(table.totals, column), summaryRowStyle));
                if (!compare)
                    return;
                const now = table.totals?.[column.id];
                const then = table.compare_totals?.[column.id];
                const change = now !== undefined && then !== undefined ? percentChange(now, then) : null;
                summary.push(cell(formatChange(change), change !== null ? changeCellStyle(summaryRowStyle, change) : summaryRowStyle));
            });
            fillTo(summary, maxColumns, summaryRowStyle);
            allRows.push(summary);
            if (compare && table.compare_totals) {
                const summaryCompared = dimensionColumns.map(() => cell("", summaryComparedStyle));
                metricColumns.forEach((column) => {
                    summaryCompared.push(cell(valueOf(table.compare_totals, column), summaryComparedStyle));
                    summaryCompared.push(cell("", summaryComparedStyle));
                });
                fillTo(summaryCompared, maxColumns, summaryComparedStyle);
                allRows.push(summaryCompared);
            }
        });
        // ── Workbook ───────────────────────────────────────────────────────
        const worksheet = {};
        allRows.forEach((row, rowIndex) => {
            row.forEach((c, colIndex) => {
                worksheet[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })] = c;
            });
        });
        worksheet["!ref"] = XLSX.utils.encode_range({
            s: { r: 0, c: 0 },
            e: { r: allRows.length - 1, c: maxColumns - 1 },
        });
        worksheet["!merges"] = merges;
        // Label columns wide, metric values wider than their % CHANGE.
        const firstTable = sections[0].report;
        const labelCount = Math.max(1, columnsOf(firstTable).dimensionColumns.length);
        const compareFirst = hasCompareOn(firstTable);
        const colWidths = [];
        for (let i = 0; i < maxColumns; i += 1) {
            if (i < labelCount)
                colWidths.push({ wch: i === 0 ? 24 : 22 });
            else
                colWidths.push({ wch: compareFirst && (i - labelCount) % 2 === 1 ? 11 : 20 });
        }
        worksheet["!cols"] = colWidths;
        worksheet["!rows"] = allRows.map((_, index) => ({
            hpt: index === 0 ? 35 : index === 1 ? 26 : index === 2 ? 22 : index === 3 ? 15 : 22,
        }));
        const workbook = XLSX.utils.book_new();
        const sheetName = (options.reportName || "Report").replace(/[\\/?*[\]:]/g, " ").slice(0, 31) || "Report";
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        const fileSafe = (text) => text.trim().replace(/[^\w.-]+/g, "_").replace(/^_+|_+$/g, "");
        // "<Client name>_<Report name>"; the client part is left out when there is none.
        const safeName = [fileSafe(options.clientName || ""), fileSafe(options.reportName || "")].filter(Boolean).join("_") || "Report";
        XLSX.writeFile(workbook, `${safeName}.xlsx`);
    }
    catch (error) {
        console.error("Export failed:", error);
        toast.info("Failed to export data. Please try again.");
    }
};
export default exportReportExcel;
